import { useCallback, useEffect, useMemo, useState } from 'react'

import { debounce } from 'es-toolkit'
import Fuse, { FuseResult, IFuseOptions } from 'fuse.js'

export const useLocalSearch = <T>(
  query: string,
  targets: Array<T> | undefined,
  fuseOptions?: IFuseOptions<T>,
) => {
  const memoizedTargets = useMemo(() => targets ?? [], [targets])
  const baseResults = useMemo(
    () =>
      targets?.map((target) => ({ item: target, score: 1 }) as FuseResult<T>),
    [memoizedTargets],
  )
  const [results, setResults] = useState<Array<FuseResult<T>>>(
    baseResults ?? [],
  )

  const fuse = useMemo(() => {
    return new Fuse(memoizedTargets, {
      includeScore: true,
      threshold: 0.23,
      distance: 100,
      ignoreLocation: true, // VERY IMPORTANT; DO NOT DELETE (this allows keyword matching to work)
      minMatchCharLength: 1,
      ...fuseOptions,
    })
  }, [memoizedTargets])

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      if (query === '') {
        setResults(baseResults ?? [])
      } else {
        setResults(fuse.search(query))
      }
    }, 100),
    [baseResults],
  )

  useEffect(() => {
    if (query === '') {
      setResults(baseResults ?? [])
    } else {
      debouncedSearch(query)
    }
  }, [query])

  useEffect(() => {
    // TODO: this is the only way i could get a bizarre infinite rerender to stop happening;
    // seems like baseResults was continuously getting updated even though it was memoized,
    // likely because of some weird rerendering behavior where we update the results array,
    // then that propagates up the stack and then forces the targets to rerender, ending up
    // in an infinite rerender
    if (baseResults === undefined) {
      return
    }
    if (query === '') {
      setResults(baseResults)
    } else {
      debouncedSearch.cancel()
      setResults(fuse.search(query))
    }
  }, [baseResults])

  return { results }
}
